import { zodResolver } from '@hookform/resolvers/zod';
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { PasswordRules } from '@/components/auth/password-rules';
import { getApiErrorMessage, getHttpStatus } from '@/lib/api-error';
import {
  resetPasswordDefaultValues,
  resetPasswordSchema,
  type ResetPasswordFormValues,
} from '@/lib/auth-schemas';
import { useConfirmForgotPasswordMutation } from '@/query/auth';
import '@/styles/route-auth.scss';

const resetPasswordSearchSchema = z.object({
  email: z.email(),
});

export const Route = createFileRoute('/reset-password')({
  validateSearch: resetPasswordSearchSchema,
  beforeLoad: ({ search }) => {
    if (!search.email) {
      throw redirect({ to: '/forgot-password' });
    }
  },
  component: ResetPasswordComponent,
});

function ResetPasswordComponent() {
  const { email } = Route.useSearch();
  const navigate = useNavigate();
  const mutation = useConfirmForgotPasswordMutation();
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = React.useState(false);

  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors, isValid },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: resetPasswordDefaultValues,
    mode: 'onChange',
  });

  const newPasswordValue = useWatch({
    control,
    name: 'newPassword',
    defaultValue: '',
  });

  const onSubmit = async (data: ResetPasswordFormValues) => {
    if (mutation.isPending) return;
    try {
      await mutation.mutateAsync({
        email,
        code: data.code,
        newPassword: data.newPassword,
      });
      toast.success('Password updated — sign in with your new password.');
      await navigate({ to: '/login', search: { email } });
    } catch (error) {
      if (getHttpStatus(error) === 400) {
        setError('code', {
          message: 'Invalid or expired verification code.',
        });
        return;
      }
    }
  };

  const errorMessage =
    mutation.isError && !errors.code
      ? getApiErrorMessage(
          mutation.error,
          'Could not reset your password. Please try again.',
        )
      : null;

  return (
    <main className="auth">
      <div className="auth__brand">World Athletics · Book of Rules</div>

      <div className="auth__card">
        <h1 className="auth__heading">Reset password.</h1>
        <p className="auth__subtitle">
          Enter the verification code sent to {email} and choose a new password.
        </p>

        <form
          className="auth__form"
          onSubmit={(e) => {
            void handleSubmit(onSubmit)(e);
          }}
          noValidate
        >
          <div
            className={`auth__field${errors.code ? ' auth__field--invalid' : ''}`}
          >
            <input
              id="reset-code"
              className="auth__field-input auth__field-input--no-toggle"
              type="text"
              placeholder=" "
              autoComplete="one-time-code"
              inputMode="numeric"
              {...register('code')}
            />
            <label className="auth__field-label" htmlFor="reset-code">
              Verification code
            </label>
            {errors.code && (
              <p className="auth__field-error">{errors.code.message}</p>
            )}
          </div>

          <div
            className={`auth__field${errors.newPassword ? ' auth__field--invalid' : ''}`}
          >
            <input
              id="reset-new-password"
              className="auth__field-input"
              type={showNewPassword ? 'text' : 'password'}
              placeholder=" "
              autoComplete="new-password"
              spellCheck={false}
              {...register('newPassword', {
                onBlur: () => {
                  setIsPasswordFocused(false);
                },
              })}
              onFocus={() => {
                setIsPasswordFocused(true);
              }}
            />
            <label className="auth__field-label" htmlFor="reset-new-password">
              New password
            </label>
            <button
              type="button"
              className="auth__field-toggle"
              onClick={() => {
                setShowNewPassword(s => !s);
              }}
              aria-label={showNewPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showNewPassword}
            >
              {showNewPassword ? (
                <EyeOffIcon size={18} />
              ) : (
                <EyeIcon size={18} />
              )}
            </button>
            {errors.newPassword && (
              <p className="auth__field-error">{errors.newPassword.message}</p>
            )}
          </div>

          {isPasswordFocused && newPasswordValue.length > 0 && (
            <PasswordRules password={newPasswordValue} />
          )}

          <div
            className={`auth__field${errors.confirmNewPassword ? ' auth__field--invalid' : ''}`}
          >
            <input
              id="reset-confirm-password"
              className="auth__field-input"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder=" "
              autoComplete="new-password"
              spellCheck={false}
              {...register('confirmNewPassword')}
            />
            <label
              className="auth__field-label"
              htmlFor="reset-confirm-password"
            >
              Confirm password
            </label>
            <button
              type="button"
              className="auth__field-toggle"
              onClick={() => {
                setShowConfirmPassword(s => !s);
              }}
              aria-label={
                showConfirmPassword ? 'Hide password' : 'Show password'
              }
              aria-pressed={showConfirmPassword}
            >
              {showConfirmPassword ? (
                <EyeOffIcon size={18} />
              ) : (
                <EyeIcon size={18} />
              )}
            </button>
            {errors.confirmNewPassword && (
              <p className="auth__field-error">
                {errors.confirmNewPassword.message}
              </p>
            )}
          </div>

          {errorMessage && (
            <div role="alert" className="auth__error">
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            className="auth__submit"
            disabled={!isValid || mutation.isPending}
          >
            {mutation.isPending ? (
              <>
                <span className="auth__submit-spinner" aria-hidden="true" />
                Resetting…
              </>
            ) : (
              'Reset password'
            )}
          </button>
        </form>

        <Link to="/login" className="auth__back-link">
          Back to sign in
        </Link>
      </div>

      <div className="auth__footer">© World Athletics</div>
    </main>
  );
}
